import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Ticket, Client, User } from '../api/supabaseEntities.js';
import { supabase } from '../api/supabaseClient.js';
import { Button } from "../components/ui/button.jsx";
import { Input } from "../components/ui/input.jsx";
import { Textarea } from "../components/ui/textarea.jsx";
import { Label } from "../components/ui/label.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.jsx";
import { Combobox } from "../components/ui/combobox.jsx";
import { Card, CardContent } from "../components/ui/card.jsx";
import { ArrowLeft, AlertCircle, RefreshCw } from 'lucide-react';

// Função utilitária para criar URLs de páginas
const createPageUrl = (pageName, params = '') => {
  const basePath = '/' + pageName.toLowerCase().replace(/ /g, '-');
  return params ? `${basePath}/${params}` : basePath;
};

// Configurações de status e prioridade (igual ao ViewTicket)
const STATUS_CONFIG = {
  open: { label: 'Aberto', color: 'bg-blue-500', textColor: 'text-blue-700', bgColor: 'bg-blue-50' },
  analysis: { label: 'Em Análise', color: 'bg-yellow-500', textColor: 'text-yellow-700', bgColor: 'bg-yellow-50' },
  in_progress: { label: 'Em Andamento', color: 'bg-orange-500', textColor: 'text-orange-700', bgColor: 'bg-orange-50' },
  resolved: { label: 'Resolvido', color: 'bg-green-500', textColor: 'text-green-700', bgColor: 'bg-green-50' },
  closed: { label: 'Fechado', color: 'bg-gray-500', textColor: 'text-gray-700', bgColor: 'bg-gray-50' }
};
const PRIORITY_CONFIG = {
  low: { label: 'Baixa', color: 'bg-green-500', textColor: 'text-green-700', bgColor: 'bg-green-50' },
  normal: { label: 'Normal', color: 'bg-blue-500', textColor: 'text-blue-700', bgColor: 'bg-blue-50' },
  high: { label: 'Alta', color: 'bg-orange-500', textColor: 'text-orange-700', bgColor: 'bg-orange-50' },
  critical: { label: 'Crítica', color: 'bg-red-500', textColor: 'text-red-700', bgColor: 'bg-red-50' }
};

// Configuração dos tipos de chamado
const TYPE_CONFIG = {
  support: { label: 'Suporte' },
  development: { label: 'Desenvolvimento' },
  project: { label: 'Projeto' },
  maintenance: { label: 'Manutenção' },
  consultation: { label: 'Consultoria' }
};

export default function EditTicket() {
  const [searchParams] = useSearchParams();
  const ticketId = searchParams.get('id');
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [clients, setClients] = useState([]);
  const [ticket, setTicket] = useState(null);
  const [systems, setSystems] = useState([]);
  const [systemModules, setSystemModules] = useState([]);
  const [error, setError] = useState(null);
  
  // Verificar se o ticket está concluído
  const isTicketCompleted = ticket && ['resolved', 'closed'].includes(ticket.status);

  useEffect(() => {
    if (ticketId) {
      loadData();
    } else {
      navigate(createPageUrl('Tickets'));
    }
  }, [ticketId]);

  // Carregar módulos quando o ticket for carregado e tiver system_id
  useEffect(() => {
    if (ticket?.system_id) {
      loadSystemModules(ticket.system_id);
    }
  }, [ticket?.system_id]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('🔄 Carregando dados do ticket:', ticketId);
      
      // Carregar usuário atual
      const userData = await User.me();
      setCurrentUser(userData);
      
      // Carregar ticket
      const ticketData = await Ticket.findById(ticketId);
      if (!ticketData) {
        throw new Error('Ticket não encontrado');
      }
      
      console.log('✅ Ticket carregado:', ticketData);
      console.log('🔍 System ID do ticket:', ticketData.system_id);
      console.log('🔍 System Module ID do ticket:', ticketData.system_module_id);
      
      // Carregar clientes
      const clientsData = await loadClients();
      
      // Carregar sistemas
      await loadSystems(userData);
      
      setTicket(ticketData);
      setClients(clientsData);
      
      // Carregar módulos se o ticket tem system_id
      if (ticketData.system_id) {
        console.log('🔄 Carregando módulos para o sistema:', ticketData.system_id);
        await loadSystemModules(ticketData.system_id, userData);
      }
      
      // Log para debug do cliente selecionado
      if (ticketData.client_id) {
        const selectedClient = clientsData.find(c => c.id === ticketData.client_id);
        console.log('🎯 Cliente do ticket:', {
          ticket_client_id: ticketData.client_id,
          selectedClient: selectedClient,
          allClients: clientsData.length
        });
      }
      
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      console.log('🔄 Carregando clientes...');
      const clientsData = await Client.list();
      console.log('✅ Clientes carregados:', clientsData?.length || 0);
      
      // Filtrar apenas clientes ativos
      const activeClients = Array.isArray(clientsData) 
        ? clientsData.filter(client => client.status === 'active' || !client.status)
        : [];
      
      console.log('👥 Clientes ativos:', activeClients.length);
      return activeClients;
    } catch (error) {
      console.error('❌ Erro ao carregar clientes:', error);
      return [];
    }
  };

  const loadSystems = async (userData = null) => {
    try {
      console.log('🔄 Carregando sistemas via RPC simples...');
      
      // Usar RPC simples que não depende de autenticação customizada
      const { data: systemsData, error: rpcError } = await supabase.rpc('list_systems');
      
      console.log('📊 Resultado RPC list_systems:', { systemsData, rpcError });
      
      if (rpcError) {
        console.warn('⚠️ RPC list_systems falhou, tentando query direta:', rpcError);
        
        // Fallback: query direta
        const { data: directData, error } = await supabase
          .from('systems')
          .select('id, name, description, version, status')
          .eq('status', 'active')
          .order('name');
        
        if (error) throw error;
        setSystems(directData || []);
        console.log(`✅ ${(directData || []).length} sistemas carregados via query direta`);
        return;
      }
      
      // A RPC list_systems retorna um array JSON diretamente
      const systemsArray = Array.isArray(systemsData) ? systemsData : [];
      console.log(`✅ ${systemsArray.length} sistemas carregados via RPC list_systems`);
      console.log('🔍 Lista de sistemas carregados:', systemsArray.map(s => ({ id: s.id, name: s.name })));
      setSystems(systemsArray);
      
    } catch (error) {
      console.error('❌ Erro ao carregar sistemas:', error);
      setSystems([]);
    }
  };

  const loadSystemModules = async (systemId) => {
    try {
      if (!systemId) {
        setSystemModules([]);
        return;
      }

      console.log('🔄 Carregando módulos via query direta para sistema:', systemId);
      
      // Query direta para buscar módulos do sistema
      const { data: modulesData, error } = await supabase
        .from('system_modules')
        .select('id, name, description, module_type, is_core, system_id')
        .eq('system_id', systemId)
        .eq('status', 'active')
        .order('name');
      
      if (error) throw error;
      
      console.log(`✅ ${(modulesData || []).length} módulos carregados para sistema ${systemId}`);
      console.log('🔍 Lista de módulos carregados:', (modulesData || []).map(m => ({ id: m.id, name: m.name })));
      setSystemModules(modulesData || []);
      
    } catch (error) {
      console.error('❌ Erro ao carregar módulos:', error);
      setSystemModules([]);
    }
  };

  const handleCallTypeChange = (callType) => {
    setTicket(prev => ({
      ...prev,
      call_type: callType,
      // Limpar campos específicos quando mudar o tipo
      service_description: callType === 'servico' ? prev.service_description : '',
      system_id: callType === 'produto' ? prev.system_id : '',
      system_module_id: callType === 'produto' ? prev.system_module_id : ''
    }));

    // Se mudou para serviço, limpar módulos
    if (callType === 'servico') {
      setSystemModules([]);
    }
  };

  const handleSystemChange = (systemId) => {
    setTicket(prev => ({
      ...prev,
      system_id: systemId,
      system_module_id: '' // Limpar módulo quando trocar sistema
    }));

    // Carregar módulos do sistema selecionado
    loadSystemModules(systemId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    
    try {
      console.log('💾 Salvando ticket:', ticket);
      
      // Preparar dados para atualização
      const updateData = {
        title: ticket.title,
        description: ticket.description,
        type: ticket.type,
        status: ticket.status,
        priority: ticket.priority,
        category: ticket.category,
        subcategory: ticket.subcategory,
        client_id: ticket.client_id,
        call_type: ticket.call_type || 'produto',
        service_description: ticket.service_description || null,
        system_id: ticket.system_id || null,
        system_module_id: ticket.system_module_id || null
      };
      
      // Se o cliente foi alterado, atualizar company_id baseado no cliente selecionado
      if (ticket.client_id) {
        const selectedClient = clients.find(c => c.id === ticket.client_id);
        if (selectedClient && selectedClient.company_id) {
          updateData.company_id = selectedClient.company_id;
        }
      }
      
      console.log('📋 Dados para atualização:', updateData);
      
      const updatedTicket = await Ticket.update(ticketId, updateData);
      
      console.log('✅ Ticket atualizado:', updatedTicket);
      navigate(createPageUrl('Tickets'));
      
    } catch (error) {
      console.error('❌ Erro ao salvar ticket:', error);
      setError('Erro ao salvar alterações: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setTicket(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRefreshClients = async () => {
    try {
      console.log('🔄 Recarregando lista de clientes...');
      const clientsData = await loadClients();
      setClients(clientsData);
    } catch (error) {
      console.error('❌ Erro ao recarregar clientes:', error);
    }
  };

  // Função para obter o nome do cliente selecionado
  const getSelectedClientName = () => {
    if (!ticket.client_id || clients.length === 0) return null;
    
    const selectedClient = clients.find(c => c.id === ticket.client_id);
    if (!selectedClient) return 'Cliente não encontrado';
    
    const clientName = selectedClient.corporate_name || selectedClient.corporate_name || 'Cliente sem nome';
    const companyName = selectedClient.company_name || selectedClient.corporate_name;
    
    return companyName ? `${clientName} (${companyName})` : clientName;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando ticket...</p>
        </div>
      </div>
    );
  }

  if (error && !ticket) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Erro ao carregar ticket</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={() => navigate(createPageUrl('Tickets'))}>
            Voltar para Tickets
          </Button>
        </div>
      </div>
    );
  }

  if (!ticket) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(createPageUrl('Tickets'))}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Editar Chamado</h1>
          <p className="text-muted-foreground mt-1">
            Editando chamado #{ticket.ticket_number || ticketId}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Erro</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {isTicketCompleted && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Chamado Concluído</h3>
              <p className="text-sm text-yellow-700 mt-1">
                Este chamado foi concluído e não pode mais ser editado. Para reabrir ou fazer alterações, 
                altere o status primeiro na visualização do chamado.
              </p>
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="title">Título</Label>
                <Input
                  id="title"
                  value={ticket.title || ''}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  disabled={isTicketCompleted}
                  required
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="client">Cliente</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRefreshClients}
                    className="h-6 w-6 p-0"
                    disabled={isLoading}
                  >
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                </div>
                <Combobox
                  id="client"
                  items={clients.map(c => ({ 
                    value: c.id, 
                    label: `${c.name || c.corporate_name || c.company_name}${c.company_name && c.company_name !== (c.name || c.corporate_name) ? ` (${c.company_name})` : ''}` 
                  }))}
                  value={ticket.client_id || ''}
                  onChange={(value) => handleInputChange('client_id', value)}
                  placeholder="Pesquise o cliente pelo nome ou nome fantasia..."
                  allowClear
                />
                
                {ticket.client_id && getSelectedClientName() && (
                  <p className="text-sm text-green-600 font-medium">
                    ✓ Cliente selecionado: {getSelectedClientName()}
                  </p>
                )}
                
                {clients.length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {clients.length} cliente(s) disponível(is)
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">Tipo</Label>
                <Select
                  value={ticket.type || ''}
                  onValueChange={(value) => handleInputChange('type', value)}
                  disabled={isTicketCompleted}
                >
                  <SelectTrigger>
                    <div className="flex items-center gap-2">
                      {TYPE_CONFIG[ticket.type]?.label || 'Selecione'}
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={ticket.status || ''}
                  onValueChange={(value) => handleInputChange('status', value)}
                  disabled={isTicketCompleted}
                >
                  <SelectTrigger>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${STATUS_CONFIG[ticket.status]?.color}`} />
                      {STATUS_CONFIG[ticket.status]?.label || ticket.status || 'Selecione'}
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${config.color}`} />
                          {config.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Prioridade</Label>
                <Select
                  value={ticket.priority || ''}
                  onValueChange={(value) => handleInputChange('priority', value)}
                  disabled={isTicketCompleted}
                >
                  <SelectTrigger>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${PRIORITY_CONFIG[ticket.priority]?.color}`} />
                      {PRIORITY_CONFIG[ticket.priority]?.label || ticket.priority || 'Selecione'}
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${config.color}`} />
                          {config.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Categoria</Label>
                <Input
                  id="category"
                  value={ticket.category || ''}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  placeholder="Ex: Hardware, Software, Rede"
                  disabled={isTicketCompleted}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subcategory">Subcategoria</Label>
                <Input
                  id="subcategory"
                  value={ticket.subcategory || ''}
                  onChange={(e) => handleInputChange('subcategory', e.target.value)}
                  placeholder="Ex: Impressora, Sistema, Configuração"
                  disabled={isTicketCompleted}
                />
              </div>

              {/* Tipo de Chamado */}
              <div className="space-y-2">
                <Label htmlFor="call_type">Tipo de Chamado</Label>
                <Select
                  value={ticket.call_type || 'produto'}
                  onValueChange={(value) => handleCallTypeChange(value)}
                  disabled={isTicketCompleted}
                >
                  <SelectTrigger>
                    <div className="flex items-center gap-2">
                      {ticket.call_type === 'servico' ? '🛠️ Serviço (Solicitação)' : '🖥️ Produto (Problema em Sistema)'}
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="produto">🖥️ Produto (Problema em Sistema)</SelectItem>
                    <SelectItem value="servico">🛠️ Serviço (Solicitação)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Campos condicionais para PRODUTO */}
              {(ticket.call_type || 'produto') === 'produto' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="system_id">Sistema</Label>
                                    <Select
                  value={ticket.system_id || ''}
                  onValueChange={(value) => handleSystemChange(value)}
                  disabled={isTicketCompleted}
                    >
                      <SelectTrigger>
                        <div className="flex items-center gap-2">
                          {systems.find(s => s.id === ticket.system_id)?.name || 'Selecione o sistema'}
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Nenhum sistema</SelectItem>
                        {systems.map((system) => (
                          <SelectItem key={system.id} value={system.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{system.name}</span>
                              {system.version && (
                                <span className="text-xs text-muted-foreground">v{system.version}</span>
                              )}
                              {system.description && (
                                <span className="text-xs text-muted-foreground">{system.description}</span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {ticket.system_id && (
                    <div className="space-y-2">
                      <Label htmlFor="system_module_id">Módulo do Sistema</Label>
                                      <Select
                  value={ticket.system_module_id || ''}
                  onValueChange={(value) => handleInputChange('system_module_id', value)}
                  disabled={isTicketCompleted}
                      >
                        <SelectTrigger>
                          <div className="flex items-center gap-2">
                            {systemModules.find(m => m.id === ticket.system_module_id)?.name || 'Selecione o módulo'}
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">Nenhum módulo específico</SelectItem>
                          {systemModules.map((module) => (
                            <SelectItem key={module.id} value={module.id}>
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {module.name} {module.is_core ? '(Core)' : ''}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {module.description || module.module_type}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Campo condicional para SERVIÇO */}
            {(ticket.call_type || 'produto') === 'servico' && (
              <div className="space-y-2">
                <Label htmlFor="service_description">Descrição do Serviço Solicitado</Label>
                <Textarea
                  id="service_description"
                  placeholder="Descreva detalhadamente o serviço que está sendo solicitado..."
                  value={ticket.service_description || ''}
                  onChange={(e) => handleInputChange('service_description', e.target.value)}
                  className="min-h-[100px]"
                  disabled={isTicketCompleted}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={ticket.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="min-h-[150px]"
                placeholder="Descreva detalhadamente o problema ou solicitação..."
                disabled={isTicketCompleted}
                required
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(createPageUrl('Tickets'))}
                disabled={isSaving}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSaving || !ticket.title || !ticket.description || isTicketCompleted}>
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Salvando...
                  </>
                ) : (
                  'Salvar Alterações'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}